import React, { useState, useEffect } from 'react';
import { Button, Checkbox, Spinner, Text } from '@fluentui/react-components';
import { Person } from '#root/queries/persons';
import { usePersonById } from '#root/queries/persons';
import { useCurrentUser } from '#root/queries/local';
import { FILE_KINDS } from '#root/utils/media';
import { DialogBox } from '#root/components/DialogBox';
import './styles.scss';
import { PersonCard } from '#root/components/PersonCardContent';

interface CompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** All public-input persons currently selected to compare. */
  publicPersons: Person[];
  victimId?: string;
  /**
   * Called when the manager confirms a match for a subset of public persons.
   * Receives the chosen publicIds.
   * Returns a promise; the dialog waits for it to resolve before deciding to close.
   */
  onMatchSelected: (publicIds: string[]) => Promise<void>;
}

interface PersonDetailsProps {
  person: Person;
  unit?: string;
}

const PersonDetails: React.FC<PersonDetailsProps> = ({ person, unit }) => {
  const portrait = person.mediaFiles?.filter(file => file.kind === FILE_KINDS.PERSONAL_IMAGE)[0];
  const tattoos = person.mediaFiles?.filter(file => file.kind === FILE_KINDS.TATTOO) || [];
  const scars = person.mediaFiles?.filter(file => file.kind === FILE_KINDS.SCAR) || [];

  return (
    <div className="compare-dialog__person">
      <div className="compare-dialog__person-header">
        <PersonCard
          person={{ ...person, pictureUrl: portrait?.url }}
          unit={unit}
        />
      </div>

      {(tattoos.length > 0 || scars.length > 0 || person.additionalInfo) && (
        <div className="compare-dialog__person-features">
          <Text weight="semibold" size={400}>Additional Features</Text>

          {tattoos.length > 0 && (
            <div className="feature-section">
              <Text weight="semibold">Tattoo Images</Text>
              <div className="image-grid">
                {tattoos.map((tattoo, index) => (
                  <img key={index} src={tattoo.url} alt={`Tattoo ${index + 1}`} />
                ))}
              </div>
            </div>
          )}

          {scars.length > 0 && (
            <div className="feature-section">
              <Text weight="semibold">Scar Images</Text>
              <div className="image-grid">
                {scars.map((scar, index) => (
                  <img key={index} src={scar.url} alt={`Scar ${index + 1}`} />
                ))}
              </div>
            </div>
          )}

          {person.additionalInfo && (
            <div className="feature-section">
              <Text weight="semibold">Additional Info</Text>
              <p>{person.additionalInfo}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Loads and renders the victim panel. */
const VictimPanel: React.FC<{ victimId: string; unit?: string }> = ({ victimId, unit }) => {
  const { data: victimPerson, isLoading } = usePersonById({ id: victimId });
  if (isLoading) return <Spinner />;
  if (!victimPerson?.item) return null;
  return <PersonDetails person={victimPerson.item} unit={unit} />;
};

export const CompareDialog: React.FC<CompareDialogProps> = ({
  open,
  onOpenChange,
  publicPersons,
  victimId,
  onMatchSelected,
}) => {
  const { data: currentUser } = useCurrentUser();
  const unit = currentUser?.unit;

  // Which public persons the manager has checked for matching. Default: all.
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [isMatching, setIsMatching] = useState(false);

  // Re-initialise checkboxes any time the dialog opens or the person list changes.
  useEffect(() => {
    setCheckedIds(new Set(publicPersons.map(p => p.id!).filter(Boolean)));
  }, [open, publicPersons]);

  const toggleCheck = (id: string) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleMatchSelected = () => {
    const selectedIds = Array.from(checkedIds);
    if (selectedIds.length === 0 || !victimId || isMatching) return;

    setIsMatching(true);
    onMatchSelected(selectedIds)
      .then(() => {
        // Determine whether any public persons remain after the match.
        // The parent removes matched cards from publicPersons after onMatchSelected resolves,
        // so we compare against what's currently shown minus what we just matched.
        const remaining = publicPersons.filter(p => p.id && !checkedIds.has(p.id));
        if (remaining.length === 0) {
          onOpenChange(false);
        }
        // If cards remain, stay open so the manager can continue comparing.
      })
      .finally(() => {
        setIsMatching(false);
      });
  };

  const checkedCount = checkedIds.size;
  const canMatch = checkedCount > 0 && !!victimId;

  return (
    <DialogBox
      className="compare-dialog"
      open={open}
      onOpenChange={onOpenChange}
      title={`Compare Victims (${publicPersons.length} Public Request${publicPersons.length !== 1 ? 's' : ''})`}
      primaryButtonText={`Match Selected (${checkedCount})`}
      primaryButtonAppearance="primary"
      isPrimaryButtonDisabled={!canMatch}
      isPrimaryButtonLoading={isMatching}
      onPrimaryButtonClick={handleMatchSelected}
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={() => onOpenChange(false)}
    >
      <div className="compare-dialog__content compare-dialog__content--multi">
        {/* Public persons panel — scrollable list on the left with checkboxes */}
        <div className="compare-dialog__public-panel">
          <div className="compare-dialog__panel-label-row">
            <Text weight="semibold" size={400} className="compare-dialog__panel-label">
              Public Requests
            </Text>
            <div className="compare-dialog__select-actions">
              <Button
                size="small"
                appearance="subtle"
                onClick={() => setCheckedIds(new Set(publicPersons.map(p => p.id!).filter(Boolean)))}
              >
                Select All
              </Button>
              <Button
                size="small"
                appearance="subtle"
                onClick={() => setCheckedIds(new Set())}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="compare-dialog__public-list">
            {publicPersons.map(person => (
              <div
                key={person.id}
                className={`compare-dialog__public-item ${checkedIds.has(person.id || '') ? 'compare-dialog__public-item--checked' : ''}`}
                onClick={() => person.id && toggleCheck(person.id)}
              >
                <Checkbox
                  className="compare-dialog__public-checkbox"
                  checked={checkedIds.has(person.id || '')}
                  onChange={() => person.id && toggleCheck(person.id)}
                  onClick={e => e.stopPropagation()}
                />
                <PersonDetails person={person} unit={unit} />
              </div>
            ))}
          </div>
        </div>

        <div className="compare-dialog__divider" />

        {/* Victim panel — static on the right */}
        <div className="compare-dialog__victim-panel">
          <Text weight="semibold" size={400} className="compare-dialog__panel-label">
            Victim
          </Text>
          {victimId ? (
            <VictimPanel victimId={victimId} unit={unit} />
          ) : (
            <Spinner />
          )}
        </div>
      </div>
    </DialogBox>
  );
};
